/**
 * Mic button for voice recording in the chat input bar.
 * Shows pulsing animation while recording, spinner while transcribing.
 */

import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { ActivityIndicator, StyleSheet, TouchableOpacity } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

import type { VoiceRecordingState } from '@/types/voice-transcription';

interface VoiceRecordingButtonProps {
  recordingState: VoiceRecordingState;
  accentColor: string;
  onPress: () => void;
}

const BUTTON_SIZE = 32;
const ICON_SIZE = 22;

export function VoiceRecordingButton({
  recordingState,
  accentColor,
  onPress,
}: VoiceRecordingButtonProps) {
  const pulseOpacity = useSharedValue(1);

  const handlePress = async () => {
    if (recordingState === 'transcribing') return;

    if (recordingState === 'idle') {
      await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      startPulse();
    } else {
      await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      stopPulse();
    }

    onPress();
  };

  const startPulse = () => {
    pulseOpacity.value = withRepeat(
      withSequence(
        withTiming(0.4, { duration: 600 }),
        withTiming(1, { duration: 600 }),
      ),
      -1, // infinite
    );
  };

  const stopPulse = () => {
    pulseOpacity.value = withTiming(1, { duration: 200 });
  };

  const pulseStyle = useAnimatedStyle(() => ({
    opacity: recordingState === 'recording' ? pulseOpacity.value : 1,
  }));

  const isRecording = recordingState === 'recording';
  const isTranscribing = recordingState === 'transcribing';
  const isDisabled = isTranscribing;

  const accessibilityLabel = isRecording
    ? 'Recording in progress, tap to stop'
    : isTranscribing
      ? 'Transcribing audio'
      : 'Start voice recording';

  return (
    <Animated.View style={pulseStyle}>
      <TouchableOpacity
        style={styles.button}
        onPress={handlePress}
        disabled={isDisabled}
        activeOpacity={0.6}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="button"
      >
        {isTranscribing ? (
          <ActivityIndicator size="small" color={accentColor} />
        ) : (
          <Ionicons
            name="mic-outline"
            size={ICON_SIZE}
            color={accentColor}
          />
        )}
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  button: {
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
});
